import { useState } from "react";
import type { FormEvent } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { ApiClientError } from "@frontend/api/client";
import { useAuth } from "@frontend/auth/AuthProvider";
import { BrandLockup } from "@frontend/components/BrandLockup";
import { Button, Field } from "@frontend/components/ui";
import { ThemeToggle } from "@frontend/theme/ThemeToggle";

export function LoginPage() {
  const { state, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  if (state === "authenticated") {
    return <Navigate replace to="/" />;
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
      const destination = (location.state as { from?: string } | null)?.from ?? "/";
      navigate(destination, { replace: true });
    } catch (caught) {
      setError(caught instanceof ApiClientError ? caught.message : "Não foi possível entrar agora.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="login-page">
      <section className="login-panel">
        <ThemeToggle className="login-theme-toggle" />
        <div className="login-card">
          <header className="login-card__brand"><BrandLockup /></header>
          <form className="login-card__body" onSubmit={(event) => void submit(event)}>
            <h1>Entrar</h1>
            <Field label="E-mail">
              <input
                autoComplete="email"
                autoFocus
                onChange={(event) => setEmail(event.target.value)}
                required
                type="email"
                value={email}
              />
            </Field>
            <Field label="Senha">
              <span className="password-input">
                <input
                  autoComplete="current-password"
                  minLength={12}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                  type={showPassword ? "text" : "password"}
                  value={password}
                />
                <button aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"} onClick={() => setShowPassword((value) => !value)} type="button">
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </span>
            </Field>

            {error ? <div className="form-alert" role="alert">{error}</div> : null}
            <Button className="login-submit" disabled={submitting} type="submit">
              {submitting ? "Entrando…" : "Entrar"}
            </Button>
          </form>
        </div>
      </section>
    </main>
  );
}
